import { useEffect, useMemo, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { Button } from '../design/primitives/Button';
import { EmptyState } from '../design/primitives/Misc';
import { useIsPhone } from '../design/useMediaQuery';
import { formatDateTime } from '../../lib/format';

// The mailbox view: rows in month bands of a date field beside a reading pane, like a mail client.
// ↑/↓ move the selection, the reading pane shows the open record. On a phone it is one pane at a
// time: the list, or the record with a Back button. Sorting, search and paging stay the list's.

export interface MailboxRow {
  id: string;
  label: ReactNode;
  secondary?: ReactNode;
  date: string | null;
}

const monthOf = (iso: string | null): string => (iso ? iso.slice(0, 7) : '');
const monthLabel = (key: string): string =>
  key ? new Date(`${key}-01T00:00:00Z`).toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' }) : 'No date';

/** Consecutive rows sharing a month form a band (the rows keep the list's order). */
export function monthBands(rows: MailboxRow[]): { key: string; label: string; rows: MailboxRow[] }[] {
  const out: { key: string; label: string; rows: MailboxRow[] }[] = [];
  for (const r of rows) {
    const k = monthOf(r.date);
    const last = out.at(-1);
    if (last && last.key === k) last.rows.push(r);
    else out.push({ key: k, label: monthLabel(k), rows: [r] });
  }
  return out;
}

export function Mailbox({
  rows,
  label,
  selectedId,
  onSelect,
  reading,
  toolbar,
  empty,
  hasMore,
  loadingMore,
  loadMore,
}: {
  rows: MailboxRow[];
  label: string;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  reading: (id: string) => ReactNode;
  toolbar?: ReactNode;
  empty?: ReactNode;
  hasMore?: boolean;
  loadingMore?: boolean;
  loadMore?: () => void;
}) {
  const phone = useIsPhone();
  const bands = useMemo(() => monthBands(rows), [rows]);
  const listRef = useRef<HTMLUListElement>(null);

  // Keep the selected row in view as the selection moves.
  useEffect(() => {
    if (!selectedId) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-id="${CSS.escape(selectedId)}"]`);
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [selectedId]);

  const onKeyDown = (e: KeyboardEvent<HTMLUListElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const i = rows.findIndex((r) => r.id === selectedId);
    const next = e.key === 'ArrowDown' ? Math.min(rows.length - 1, i + 1) : Math.max(0, i - 1);
    const target = rows[next < 0 ? 0 : next];
    if (target) onSelect(target.id);
  };

  const list = (
    <div className="hf-mailbox-list">
      {toolbar && <div className="hf-mailbox-tools">{toolbar}</div>}
      {rows.length === 0 ? (
        empty ?? <EmptyState title="Nothing here" icon="inbox" />
      ) : (
        <ul ref={listRef} role="listbox" aria-label={label} tabIndex={0} onKeyDown={onKeyDown} className="hf-mailbox-rows">
          {bands.map((b) => (
            <li key={b.key || 'none'} role="presentation">
              <div className="hf-mailbox-band" role="presentation">{b.label}</div>
              <ul role="presentation">
                {b.rows.map((r) => (
                  <li
                    key={r.id}
                    data-id={r.id}
                    role="option"
                    aria-selected={r.id === selectedId}
                    className={['hf-mailbox-row', r.id === selectedId ? 'selected' : ''].join(' ')}
                    onClick={() => onSelect(r.id)}
                  >
                    <span className="hf-mailbox-label">{r.label}</span>
                    {r.date && <time className="hf-muted hf-num" dateTime={r.date}>{formatDateTime(r.date)}</time>}
                    {r.secondary && <span className="hf-mailbox-secondary hf-muted">{r.secondary}</span>}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
      {hasMore && (
        <Button variant="ghost" size="sm" busy={loadingMore} onClick={loadMore}>
          Load more
        </Button>
      )}
    </div>
  );

  const pane = selectedId ? (
    <div className="hf-mailbox-reading" aria-label="Reading pane" role="region">
      {phone && (
        <Button variant="ghost" size="sm" icon="arrow-left" onClick={() => onSelect(null)}>
          Back to the list
        </Button>
      )}
      {reading(selectedId)}
    </div>
  ) : (
    <div className="hf-mailbox-reading empty" aria-label="Reading pane" role="region">
      <EmptyState title="Nothing open" hint="Pick one from the list (↑/↓ move)." icon="inbox" />
    </div>
  );

  if (phone) return <div className="hf-mailbox phone">{selectedId ? pane : list}</div>;
  return (
    <div className="hf-mailbox">
      {list}
      {pane}
    </div>
  );
}
